import '@ooka_usa_store/styles/main.scss'
import '@styles/chrome-bug.css'
import 'slick-carousel/slick/slick.css'
import 'slick-carousel/slick/slick-theme.css'
import 'swiper/swiper-bundle.css'
import React, { FC, useEffect, useState } from 'react'
import type { AppProps } from 'next/app'
import { appWithTranslation } from 'next-i18next'
import { useRouter } from 'next/router'

import { ManagedUIContext } from '@corratech/pylot-ui/context'
import { ManagedAuthContext } from '@corratech/pylot-auth-manager'
import { Head } from '@components/common'
import { useCreateCart } from '@corratech/pylot-cart-manager'
import { Toast } from '@corratech/pylot-ui'
import { TagContextInit } from '@components/OOKA/pylot-tag-manager'
import { useGTM } from '@components/OOKA/pylot-gtm/src/useGTM'
import { LoadingIndicator } from '@components/LoadingIndicator'
import { useStoreConfig } from '@config/hooks/useStoreConfig'
import '../sentry.client.config'
import dynamic from 'next/dynamic'
import { Maintenance } from '@components/Maintenance'
import { YotpoLoyalty } from 'components/YotpoLoyalty/YotpoLoyalty'
import { AlfakherContext } from 'components/alfakherContext/AlfakherContext'
import { useCookies } from 'react-cookie'
import { getDomainName } from 'preload'
import { AgeValidationPopup } from '@components/AgeValidationPopup/AgeValidationPopup'
import { SocialLoginScript } from '@components/SocialLogin/SocialLoginScript'
import '@styles/slick-override.scss'
import AOS from 'aos'
import 'aos/dist/aos.css'
const Noop: FC = ({ children }) => <>{children}</>

function MyApp({ Component, pageProps }: AppProps) {
    const router = useRouter()
    const Layout = (Component as any).Layout || Noop
    const authRequired = (Component as any).Protected || false

    const config = useStoreConfig()
    const [cookies, setCookie] = useCookies(['cje'])
    const [isLoader, setIsLoader] = useState(false)

    const Signifyd = dynamic(
        () => import('@components/alfakher/pylot-signifyd/src/Signifyd')
    )

    useEffect(() => storePathValues, [router.asPath])
    useEffect(() => {
        AOS.init()
    }, [])

    function storePathValues() {
        const storage = globalThis?.sessionStorage
        if (!storage) return
        const prevPath = storage.getItem('currentPath')
        storage.setItem('prevPath', prevPath || '')
        storage.setItem('currentPath', globalThis.location.pathname)
    }

    const setCJCookie = () => {
        const params = new URLSearchParams(window.location.search)
        const cjevent = params.get('cjevent')
        if (cjevent) {
            const dt = new Date()
            const baseUrl = config?.base?.url?.baseUrl
            const domain = `.${getDomainName(baseUrl)}`
            setCookie('cje', cjevent, {
                path: '/',
                httpOnly: false,
                secure: true,
                sameSite: 'none',
                expires: new Date(dt.getTime() + 569401 * 60000),
                domain: domain
            })
        }
    }

    useEffect(() => {
        setCJCookie()
        document.body.classList?.remove('loading')
    }, [])

    useCreateCart()

    const { gtmHandler, gtmInitScript } = useGTM()
    return (
        <>
            <Head />
            {config.base.signifyd.enabled === true && <Signifyd />}
            <Maintenance isMaintenanceEnabled>
                <AlfakherContext>
                    <TagContextInit
                        handlers={[gtmHandler]}
                        scripts={[gtmInitScript]}
                    >
                        <ManagedUIContext LoadingIndicator={LoadingIndicator}>
                            <ManagedAuthContext>
                                <AgeValidationPopup />
                                <SocialLoginScript />
                                <Layout
                                    pageProps={pageProps}
                                    authRequired={authRequired}
                                    setIsLoader={setIsLoader}
                                >
                                    {config.base.yotpoLoyalty.enabled &&
                                        config.base.yotpoLoyalty.guid && (
                                            <YotpoLoyalty
                                                guid={
                                                    config.base.yotpoLoyalty
                                                        .guid
                                                }
                                            />
                                        )}
                                    <Toast isToastFixed />
                                    <Component {...pageProps} />
                                </Layout>
                            </ManagedAuthContext>
                        </ManagedUIContext>
                    </TagContextInit>
                </AlfakherContext>
            </Maintenance>
        </>
    )
}

export default appWithTranslation(MyApp)
